import { useCallback, useMemo, useState } from 'react'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Typography } from '@/components/ui/typography'
import { Trash2 } from 'lucide-react'
import { OperatorSelect } from './operator-select'
import { ENUM_OPERATORS } from './filter-types'
import { getOptionRenderer } from './filter-option-renderers'
import type { FilterOperator } from '@/types/api'

// Shared checkbox styling for filter options
const FILTER_CHECKBOX_CLASS =
  'size-3.5 rounded-[3px] border-primary/20 data-[state=checked]:size-4 data-[state=checked]:bg-[#B86846] data-[state=checked]:border-[#B86846] data-[state=checked]:[&>[data-slot=checkbox-indicator]>svg]:text-white'

interface EnumFilterEditorProps {
  options: string[]
  selectedValues: string[]
  operator: FilterOperator
  onValuesChange: (values: string[]) => void
  onOperatorChange: (operator: FilterOperator) => void
  onRemove?: () => void
  label?: string
  searchPlaceholder?: string
  fieldName?: string
}

export function EnumFilterEditor({
  options,
  selectedValues,
  operator,
  onValuesChange,
  onOperatorChange,
  onRemove,
  label,
  searchPlaceholder = 'Search...',
  fieldName,
}: EnumFilterEditorProps) {
  const [search, setSearch] = useState('')

  const OptionRenderer = useMemo(
    () => getOptionRenderer(fieldName ?? ''),
    [fieldName]
  )

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options
    const searchLower = search.toLowerCase()
    return options.filter((opt) => opt.toLowerCase().includes(searchLower))
  }, [options, search])

  const isAllSelected = useMemo(() => {
    return (
      filteredOptions.length > 0 &&
      filteredOptions.every((opt) => selectedValues.includes(opt))
    )
  }, [filteredOptions, selectedValues])

  const handleToggleValue = useCallback(
    (value: string) => {
      const newValues = selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value]
      onValuesChange(newValues)
    },
    [selectedValues, onValuesChange]
  )

  const handleToggleSelectAll = useCallback(() => {
    if (isAllSelected) {
      // Deselect all filtered options
      const newValues = selectedValues.filter(
        (v) => !filteredOptions.includes(v)
      )
      onValuesChange(newValues)
    } else {
      // Select all filtered options (merge with existing)
      const newValues = [...new Set([...selectedValues, ...filteredOptions])]
      onValuesChange(newValues)
    }
  }, [isAllSelected, filteredOptions, selectedValues, onValuesChange])

  return (
    <div className="flex flex-col px-2 pt-2">
      {/* Row 1: Operator select */}
      <div className="mb-2">
        <OperatorSelect
          value={operator}
          options={ENUM_OPERATORS}
          onChange={onOperatorChange}
          label={label}
          className="h-8"
        />
      </div>
      {/* Row 2: Search input + trash */}
      <div className="flex items-center gap-2 mb-2">
        <div className="flex-1 flex items-center h-9 rounded-md border border-primary/20 px-3">
          <span className="text-primary/70 mr-1">|</span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="flex-1 bg-transparent text-xs text-primary placeholder:text-primary/70 outline-none font-univers"
          />
        </div>
        {onRemove && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemove}
            className="h-9 w-9 shrink-0"
          >
            <Trash2 className="h-4 w-4 text-destructive/50" />
          </Button>
        )}
      </div>
      <Command>
        <CommandList>
          <CommandEmpty>No options found.</CommandEmpty>
          <CommandGroup>
            {/* Row 3: Select All */}
            <CommandItem
              className="cursor-pointer"
              onSelect={handleToggleSelectAll}
            >
              <Checkbox
                checked={isAllSelected}
                className={FILTER_CHECKBOX_CLASS}
              />
              <Typography variant="body-sm" className="font-normal leading-5">
                Select All
              </Typography>
            </CommandItem>
            {/* Dashed separator */}
            <div className="py-1">
              <div className="border-t border-dashed border-primary/30" />
            </div>
            {filteredOptions.map((option, index) => {
              const isSelected = selectedValues.includes(option)
              return (
                <CommandItem
                  key={`${option}-${index}`}
                  value={option}
                  onSelect={() => handleToggleValue(option)}
                  className="cursor-pointer"
                >
                  <Checkbox
                    checked={isSelected}
                    className={FILTER_CHECKBOX_CLASS}
                  />
                  {/* Let each renderer handle its own formatting */}
                  <OptionRenderer value={option} label={option} />
                </CommandItem>
              )
            })}
          </CommandGroup>
        </CommandList>
      </Command>
    </div>
  )
}
